import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, finalize, map } from 'rxjs';
import { reportInvalidFormFields } from '@app/utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BreadcrumbService, ModalService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ApDocumentQuickViewModalComponent } from '../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import {
  ApPaymentRequest,
  ApPaymentRequestInsert,
  ApPaymentRequestLine,
  ApPaymentRequestLineInsert,
  ApPaymentRequestStatus,
  ApPaymentRequestUpdate,
} from '../../../models/ap-payment-request/ap-payment-request';
import { ApPaymentRequestFormGroup, ApPaymentRequestLineFormGroup } from '../../../models/ap-payment-request/ap-payment-request-form';
import { ApPaymentRequestProxyService } from '../../../proxies/ap-payment-request.proxy.service';
import { ApDocumentProxyService } from '../../../proxies/ap-document.proxy.service';
import { ApAdvanceProxyService } from '../../../proxies/ap-advance.proxy.service';
import {
  ApDocumentType,
  Currency,
  EntityKey,
  PayableDocument,
  distributePayable,
  isPayableBaseGroup,
  payableGroupRefs,
  payableRowDefault,
} from '../../../models/ap-document/ap-document';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { PoReceiptProxyService } from '../../../proxies/po-receipt.proxy.service';
import { PoReceiptCoverage, PoReceiptCoverageRequest } from '../../../models/po-receipt/po-receipt';
import { watchReceivedCoverage } from '../../../utilities/received-coverage.util';
import { Business, BusinessEntityType } from '../../../models/business';
import { ProviderBankAccount } from '../../../models/provider/provider-details';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { AccountsPayableRoles } from '../../../models/roles/roles-group';
import { currencyLabelFromApi } from '../../../utilities/currency.util';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-ap-payment-request-details',
  templateUrl: './ap-payment-request-details.component.html',
  styleUrl: './ap-payment-request-details.component.scss',
})
export class ApPaymentRequestDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(ApPaymentRequestProxyService);
  private readonly documentService = inject(ApDocumentProxyService);
  private readonly advanceService = inject(ApAdvanceProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly poReceiptService = inject(PoReceiptProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly auth = inject(UserAuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly ApPaymentRequestStatus = ApPaymentRequestStatus;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApPaymentRequestStatus.Draft);
  headerTitle = signal<string>(this.t('ApPaymentRequest_New', 'New payment request'));
  spawnedPaymentId = signal<string | null>(null);
  /** Cash requests this request has been planned into (#4798). Maintained by the cash request — read-only
   * here, and empty until someone plans it. */
  cashRequestReferences = signal<EntityKey[]>([]);

  bankOptions = signal<SelectOption[]>([]);
  /** The provider behind the form control, kept separately so the code/name readout and the reload action
   * still work once the control is disabled (it is locked after creation). */
  selectedProvider = signal<Business | null>(null);

  total = signal(0);
  /** Cash actually requested = Σ (amountToPay − advanceToUse); advance = Σ advanceToUse (#4349). */
  totalCash = signal(0);
  totalAdvance = signal(0);
  /** The request total in functional (reporting) currency — Σ of each line's own converted amount, never the
   * source total times one rate: invoices booked on different days carry different rates. */
  totalFunctional = signal(0);
  /** Advance and cash in functional currency — the only figures that stay addable when the selected rows do
   * not share a currency. */
  totalAdvanceFunctional = signal(0);
  totalCashFunctional = signal(0);
  /** Distinct currencies across the selected rows. More than one means the raw sums below cannot be shown:
   * adding 10,000 HNL to 2,000 USD produces a number that is not money in any currency. */
  readonly selectedCurrencies = signal<number[]>([]);
  readonly isMixedCurrency = computed(() => this.selectedCurrencies().length > 1);
  /** The one currency the selection is in, when it has one. */
  readonly selectionCurrencyLabel = computed(() => {
    const currencies = this.selectedCurrencies();
    return currencies.length === 1 ? (this.currencyLabels[currencies[0]] ?? '') : '';
  });
  selectedCount = signal(0);

  /** Per document: would paying its amount take its PO past what was received (#5662)? Advice only — the
   * row stays selectable; the API decides at post. */
  readonly receivedCoverage = signal<Map<string, PoReceiptCoverage>>(new Map());
  /** The SELECTED rows' check results — the banner above the totals names the ones over what was received. */
  readonly selectedCoverage = computed(() => {
    this.selectedCount();
    const coverage = this.receivedCoverage();
    return this.lines.controls
      .filter((row) => row.controls.selected.value)
      .map((row) => coverage.get(row.controls.apDocumentReference.value))
      .filter((c): c is PoReceiptCoverage => c !== undefined);
  });

  // ── Finding a document in a long list: search + "selected only" + client-side paging ──────────────────
  // All of it is a VIEW over the form array. Every row stays in `lines` whether or not it is shown, so a row
  // hidden by the filter or on another page keeps its selection and amounts, and totals, advances and the
  // received check still see everything.

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly search = toSignal(this.searchControl.valueChanges, { initialValue: '' });
  readonly selectedOnlyControl = new FormControl<boolean>(false, { nonNullable: true });
  private readonly selectedOnly = toSignal(this.selectedOnlyControl.valueChanges, { initialValue: false });
  /** Bumped whenever the rows change (loaded, cleared, edited): `lines` is a FormArray, not a signal. */
  private readonly rowsVersion = signal(0);
  /** The table's current first row. Reset on every filter change so a narrowed list is not left on page 4. */
  readonly firstRow = signal(0);
  readonly pageSize = 25;

  /** Rows that pass the search and the "selected only" toggle — what the table pages through. Open credits are
   *  not invoices to pay (#6324): they have their own panel, "Créditos del proveedor". */
  readonly visibleLines = computed(() => {
    this.rowsVersion();
    this.selectedCount(); // select-all writes without emitting; the totals signal is what moves
    const term = this.search().trim().toLowerCase();
    const selectedOnly = this.selectedOnly();
    return this.lines.controls.filter(
      (row) =>
        !row.controls.isCredit.value && (!selectedOnly || row.controls.selected.value) && (term === '' || this.rowMatches(row, term)),
    );
  });

  /**
   * #6324 — the provider's open credits ("saldo a favor"): credit notes their invoice could not absorb, and
   * financial credit notes. Used like an advance: the requester picks which and how much, nothing is selected
   * by default, and whatever is not used stays open for a later request.
   */
  readonly creditLines = computed(() => {
    this.rowsVersion();
    this.selectedCount();
    return this.lines.controls.filter((row) => row.controls.isCredit.value);
  });

  /** What a credit row applies, as a positive figure — the row stores it negative, as the API takes it. */
  creditApplied(row: FormGroup<ApPaymentRequestLineFormGroup>): number {
    return row.controls.selected.value ? -(row.controls.amountToPay.value ?? 0) : 0;
  }

  /** What stays open on a credit after this request. */
  creditLeftOpen(row: FormGroup<ApPaymentRequestLineFormGroup>): number {
    return Math.round((-(row.controls.netPayable.value ?? 0) - this.creditApplied(row)) * 100) / 100;
  }

  onCreditAppliedChange(row: FormGroup<ApPaymentRequestLineFormGroup>, value: number | null): void {
    row.controls.amountToPay.setValue(-Math.abs(value ?? 0));
  }

  /** Credits applied across the request, in the functional currency — what the charges must exceed (#4476). */
  readonly creditAppliedFunctional = signal(0);
  readonly chargesFunctional = signal(0);
  readonly creditsExceedCharges = computed(
    () => this.creditAppliedFunctional() > 0 && this.creditAppliedFunctional() >= this.chargesFunctional(),
  );
  readonly isFiltered = computed(() => this.search().trim() !== '' || this.selectedOnly());

  /** Our AP number, the provider's fiscal number, or any PO the document draws from. */
  private rowMatches(row: FormGroup<ApPaymentRequestLineFormGroup>, term: string): boolean {
    const poNumbers = (this.payableFor(row.controls.apDocumentReference.value)?.document.purchaseOrderKeys ?? []).map(
      (k) => k.number ?? '',
    );
    return [row.controls.invoiceNumber.value, row.controls.fiscalNumber.value, ...poNumbers].some((value) =>
      value.toLowerCase().includes(term),
    );
  }

  /** Rows "select all" acts on: the VISIBLE ones the user may select. Ticking "all" under a search ticks
   * what the search found, not forty invoices off-screen — and a withholding-blocked row is never one. */
  private selectableLines() {
    return this.visibleLines().filter((row) => !row.controls.withholdingBlocked.value);
  }

  /** Header checkbox state: true only when every selectable row is on. */
  readonly allSelected = computed(() => {
    // Depends on selectedCount so it recomputes with the same signal the totals do.
    this.selectedCount();
    const selectable = this.selectableLines();
    return selectable.length > 0 && selectable.every((row) => row.controls.selected.value);
  });

  /** Select or clear every visible selectable invoice at once (#5224). Blocked and hidden rows are left alone. */
  toggleSelectAll(checked: boolean): void {
    for (const row of this.selectableLines()) {
      row.controls.selected.setValue(checked, { emitEvent: false });
    }
    this.recomputeTotals();
  }
  /** Base doc id → its payable aggregate (notes + withholding + net), for the adjustment flag + resumen (#4516). */
  private readonly payableByDoc = new Map<string, PayableDocument>();
  /** PO reference → the currency its invoices are in, so advance in another currency is not offered
   *  against them (#5373). Filled alongside the availability map below. */
  private readonly currencyOfPo = new Map<string, Currency>();

  /** PO reference → advance still available against it (#4349), from posted Anticipos. */
  private readonly advanceAvailByPo = new Map<string, number>();
  /** A reopened request's persisted lines — used to re-add any that the live payable search no longer
   * returns (fully-paid / status-changed docs) so the selection stays visible (#4457). Cleared once applied. */
  private reopenPersistedLines: ApPaymentRequestLine[] = [];
  /** Row-expansion state, keyed by document id — shows the resumen inline. */
  expandedRows: Record<string, boolean> = {};

  readonly isApprover = this.auth.roles.includes(AccountsPayableRoles.AccountsPayableApprover);

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  private currency = signal<number>(0);
  /** Reporting currency the functional figures are expressed in (USD in practice). Taken from the documents
   * rather than assumed, so a USD-only reading is asserted instead of hard-coded. */
  private functionalCurrency = signal<number>(0);

  readonly isDraft = computed(() => this.status() === ApPaymentRequestStatus.Draft);
  readonly isPendingApproval = computed(() => this.status() === ApPaymentRequestStatus.PendingApproval);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());
  readonly isEditable = computed(() => this.isDraft() || (this.isPendingApproval() && this.isApprover));

  readonly form: FormGroup<ApPaymentRequestFormGroup> = this.formBuilder.group<ApPaymentRequestFormGroup>({
    provider: new FormControl<Business | null>(null, [Validators.required]),
    selectedProviderBankId: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    lines: this.formBuilder.array<FormGroup<ApPaymentRequestLineFormGroup>>([]),
  });

  /** Kept whole, not just as options: the destination's CURRENCY decides what the money can leave as. */
  private readonly providerBanks = signal<ProviderBankAccount[]>([]);
  private readonly selectedBankId = toSignal(this.form.controls.selectedProviderBankId.valueChanges, { initialValue: null });

  /** Currency of the account the money is being sent to; null when none is chosen. */
  readonly destinationCurrency = computed(() => {
    const id = this.selectedBankId() ?? this.form.controls.selectedProviderBankId.value;
    return this.providerBanks().find((b) => b.id === id)?.currency ?? null;
  });

  get lines(): FormArray<FormGroup<ApPaymentRequestLineFormGroup>> {
    return this.form.controls.lines;
  }

  private readonly fieldLabels: Record<string, string> = {
    provider: 'ApPayment_Provider',
    lines: 'ApPaymentRequest_Lines',
  };

  constructor() {
    this.statusLabels = this.enumService.getEnum('ApPaymentRequestStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.lines.valueChanges.subscribe(() => {
      this.recomputeTotals();
      this.rowsVersion.update((v) => v + 1);
    });
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.firstRow.set(0));
    this.selectedOnlyControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.firstRow.set(0));
    watchReceivedCoverage(this.lines.valueChanges.pipe(map(() => this.coverageRequests())), this.poReceiptService)
      .pipe(takeUntilDestroyed())
      .subscribe((coverage) => this.receivedCoverage.set(coverage));
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadRequest(id);
    } else {
      this.breadcrumbService.setCurrent('Common_New');
    }
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  currencyLabel(): string {
    return this.currencyLabels[this.currency()] ?? '';
  }

  /** The currency a row's own figures are in. */
  lineCurrencyLabel(line: FormGroup<ApPaymentRequestLineFormGroup>): string {
    return this.currencyLabels[line.controls.currency.value] ?? '';
  }

  /** Currency code of the chosen destination account. */
  destinationLabel(): string {
    const currency = this.destinationCurrency();
    return currency === null ? '' : (this.currencyLabels[currency] ?? '');
  }

  functionalLabel(): string {
    return this.currencyLabels[this.functionalCurrency()] ?? '';
  }

  statusLabelText(): string {
    return this.statusLabels[this.status()] ?? '';
  }

  statusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (this.status()) {
      case ApPaymentRequestStatus.Approved:
        return 'success';
      case ApPaymentRequestStatus.PendingApproval:
        return 'warn';
      case ApPaymentRequestStatus.Rejected:
        return 'danger';
      case ApPaymentRequestStatus.Cancelled:
        return 'secondary';
      default:
        return 'info';
    }
  }

  // ---- provider ----
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  onProviderSelected(provider: Business): void {
    if (!provider?.id) return;
    this.selectedProvider.set(provider);
    this.loadProviderBanks(provider);
    this.loadPayableInvoices(provider.id);
  }

  onProviderCleared(): void {
    this.selectedProvider.set(null);
    this.bankOptions.set([]);
    this.form.controls.selectedProviderBankId.setValue(null);
    this.lines.clear();
    this.recomputeTotals();
  }

  /**
   * Re-fetches the selected provider's master data — in practice, its bank accounts. A provider is often
   * picked before its banking details exist, and without this the only way to pick up a just-added account
   * was to reload the page, which on a draft means losing the amounts typed so far.
   *
   * <p>Deliberately touches nothing but the bank list: the lines, amounts and notes are the user's work.</p>
   */
  reloadProvider(): void {
    const current = this.selectedProvider();
    if (!current?.id) return;
    this.startLoading();
    this.businessService
      .getBusinessById(current.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const provider = response.data;
          if (!provider) return;
          this.selectedProvider.set(provider);
          const banks = provider.providerDetails?.banks ?? [];
          this.applyProviderBanks(banks);
          if (banks.length === 0) {
            GlobalMessageUtil.warn(
              this.t('ApPaymentRequest_Provider_Reloaded_Title', 'Provider refreshed'),
              this.t('ApPaymentRequest_NoBanks', 'This provider still has no bank accounts registered.'),
            );
            return;
          }
          GlobalMessageUtil.success(
            this.t('ApPaymentRequest_Provider_Reloaded_Title', 'Provider refreshed'),
            this.t('ApPaymentRequest_Provider_Reloaded', 'The provider bank accounts were reloaded.'),
          );
        },
        error: () => undefined,
      });
  }

  private applyProviderBanks(banks: ProviderBankAccount[]): void {
    this.providerBanks.set(banks);
    this.bankOptions.set(banks.filter((b) => !!b.id).map((b) => ({ label: this.bankLabel(b), value: b.id! })));
    const primary = banks.find((b) => b.isPrimary) ?? banks[0];
    if (primary?.id && !this.form.controls.selectedProviderBankId.value) {
      this.form.controls.selectedProviderBankId.setValue(primary.id);
    }
  }

  private loadProviderBanks(provider: Business): void {
    // The search result usually carries providerDetails; fall back to a fetch by id.
    if (provider.providerDetails?.banks?.length) {
      this.applyProviderBanks(provider.providerDetails.banks);
      return;
    }
    if (!provider.id) return;
    this.businessService.getBusinessById(provider.id).subscribe({
      next: (response) => this.applyProviderBanks(response.data?.providerDetails?.banks ?? []),
      error: () => this.bankOptions.set([]),
    });
  }

  /** Alias/bank, currency, account number. The currency is the field that decides whether paying this
   *  account converts, so it belongs in the label the approver picks from. */
  private bankLabel(bank: ProviderBankAccount): string {
    const parts = [bank.alias || bank.bankName, currencyLabelFromApi(bank.currency), bank.accountNumber].filter((s) => s);
    return parts.join(' · ') || (bank.id ?? '');
  }

  // ---- payable invoices ----
  /** Build one GROUPED row per payable document (#4516): a base document carries its notes inside the resumen,
   * a standalone note is its own row. `savedByRef` (a reopened request's per-document amounts) folds back into
   * the group amount = the sum of its members' saved amounts. */
  private loadPayableInvoices(
    providerId: string,
    savedByRef?: Map<string, number>,
    savedAdvanceByRef?: Map<string, number>,
    savedNoteByRef?: Map<string, string>,
  ): void {
    this.startLoading();
    this.documentService
      .getPayableAggregated(providerId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.lines.clear();
          this.payableByDoc.clear();
          this.expandedRows = {};
          const payables = response.data ?? [];
          if (payables.length) {
            // Only the reporting currency is a property of the request as a whole. The transaction currency
            // belongs to each document — taking it from payables[0] labelled every row with the first
            // document's currency, so a USD invoice under an HNL one read as HNL.
            this.functionalCurrency.set(payables[0].document.functionalCurrency ?? 0);
          }
          // A request past editing is a record, not a worksheet: show only the documents it carries.
          // Rendering the provider's other open invoices as unchecked rows read as choices still on the
          // table, on a screen where nothing can be chosen any more.
          const requestOnly = !!savedByRef && !this.isEditable();
          for (const p of payables) {
            const baseId = p.document.id ?? '';

            // A reopened request's saved amounts are per-document; fold the group's members back into one amount.
            const savedAmounts = savedByRef
              ? payableGroupRefs(p)
                  .map((ref) => savedByRef.get(ref))
                  .filter((v): v is number => v !== undefined)
              : [];
            const hasSaved = savedAmounts.length > 0;
            if (requestOnly && !hasSaved) continue;
            this.payableByDoc.set(baseId, p);
            const netDefault = payableRowDefault(p);
            const amount = hasSaved ? Math.round(savedAmounts.reduce((s, v) => s + v, 0) * 100) / 100 : netDefault;
            const isCredit = !isPayableBaseGroup(p) && p.document.documentType === ApDocumentType.CreditNote;
            // #4488/#4842 — a document still waiting on its Constancia cannot go on a request. Shown but
            // unselectable, and never auto-selected: the default below is `true` for everything else, which
            // would otherwise silently put a document on the request that the API rejects on save.
            const blocked = p.withholdingSatisfied === false;

            this.lines.push(
              this.formBuilder.group<ApPaymentRequestLineFormGroup>({
                apDocumentReference: new FormControl<string>(baseId, { nonNullable: true }),
                invoiceNumber: new FormControl<string>(p.document.number ?? '', { nonNullable: true }),
                // Already on the aggregated payload — no extra call needed (#4788).
                fiscalNumber: new FormControl<string>(p.document.fiscalNumber ?? '', { nonNullable: true }),
                dueDate: new FormControl<string | null>(p.document.dueDate ?? null),
                netPayable: new FormControl<number>(netDefault, { nonNullable: true }),
                // #5224 — nothing is selected on a fresh load. Auto-selecting every payable invoice
                // made "pay everything" the path of least resistance on a screen that moves money;
                // choosing is now deliberate. Reopening a SAVED request still restores what was saved,
                // which is a different question from what a new request should default to.
                selected: new FormControl<boolean>(blocked ? false : savedByRef ? hasSaved : false, {
                  nonNullable: true,
                }),
                withholdingBlocked: new FormControl<boolean>(blocked, { nonNullable: true }),
                amountToPay: new FormControl<number>(amount, { nonNullable: true }),
                isCredit: new FormControl<boolean>(isCredit, { nonNullable: true }),
                // The rate the document was booked at — 1 keeps a rate-less document honest (source = functional).
                exchangeRate: new FormControl<number>(p.document.exchangeRate || 1, { nonNullable: true }),
                currency: new FormControl<number>(p.document.currency ?? 0, { nonNullable: true }),
                docWithholding: new FormControl<number>(p.withheldAmount ?? 0, { nonNullable: true }),
                // Restored on reopen; otherwise auto-filled once availability loads.
                advanceToUse: new FormControl<number>(savedAdvanceByRef?.get(baseId) ?? 0, { nonNullable: true }),
                advanceAvailable: new FormControl<number>(0, { nonNullable: true }),
                // Any member of the group carries the group's note (#4787) — the base first, since a group
                // whose base was fully absorbed still has its notes as members.
                note: new FormControl<string | null>(
                  savedNoteByRef
                    ? (payableGroupRefs(p)
                        .map((ref) => savedNoteByRef.get(ref))
                        .find((n) => !!n) ?? null)
                    : null,
                ),
              }),
            );
          }
          // Fetch the advance available on these invoices' POs, then cap/auto-fill "advance to use" (#4349).
          this.loadAdvances(!!savedByRef);
          if (!this.isEditable()) this.form.disable({ emitEvent: false });
        },
        error: () => {
          this.lines.clear();
          this.payableByDoc.clear();
          this.recomputeTotals();
        },
      });
  }

  /** Fetch the posted advances (Anticipos) available on the loaded invoices' POs, then apply the caps (#4349).
   * On a reopened request the saved "advance to use" is preserved; on a new one each row auto-fills. */
  private loadAdvances(isReopen: boolean): void {
    this.advanceAvailByPo.clear();
    this.currencyOfPo.clear();
    const poRefs = new Set<string>();
    for (const p of this.payableByDoc.values()) {
      if (!isPayableBaseGroup(p)) continue;
      for (const k of p.document.purchaseOrderKeys ?? []) {
        if (!k.reference) continue;
        poRefs.add(k.reference);
        if (p.document.currency !== undefined) this.currencyOfPo.set(k.reference, p.document.currency);
      }
    }
    if (poRefs.size === 0) {
      this.applyAdvanceCaps(isReopen);
      return;
    }
    this.advanceService.getPostedByPurchaseOrders([...poRefs]).subscribe({
      next: (response) => {
        for (const advance of response.data ?? []) {
          for (const alloc of advance.allocations ?? []) {
            // Only advance held in the SAME currency can settle the invoice (#5373). The payment's matcher
            // enforces it; without the same test here the request would offer advance the payment then
            // refuses, and the failure surfaces at post on a request that looked fine when approved.
            const invoiceCurrency = this.currencyOfPo.get(alloc.purchaseOrderReference);
            if (invoiceCurrency !== undefined && alloc.currency !== undefined && alloc.currency !== invoiceCurrency) {
              continue;
            }
            if (poRefs.has(alloc.purchaseOrderReference)) {
              this.advanceAvailByPo.set(
                alloc.purchaseOrderReference,
                (this.advanceAvailByPo.get(alloc.purchaseOrderReference) ?? 0) + (alloc.remainingAmount ?? 0),
              );
            }
          }
        }
        this.applyAdvanceCaps(isReopen);
      },
      error: () => this.applyAdvanceCaps(isReopen),
    });
  }

  /** Per base-group row, set the advance cap = min(base balance, amount to pay, PO advance still available given
   * earlier rows' use) — a shared running balance so several invoices of one PO can't over-draw (#4349). Auto-fills
   * "advance to use" to the cap on a new request; clamps a reopened request's saved value to it. */
  private applyAdvanceCaps(isReopen: boolean): void {
    const running = new Map(this.advanceAvailByPo);
    for (const row of this.lines.controls) {
      const p = this.payableByDoc.get(row.controls.apDocumentReference.value);
      if (!p || !isPayableBaseGroup(p)) {
        row.controls.advanceAvailable.setValue(0, { emitEvent: false });
        row.controls.advanceToUse.setValue(0, { emitEvent: false });
        continue;
      }
      const poRefs = (p.document.purchaseOrderKeys ?? []).map((k) => k.reference).filter((r): r is string => !!r);
      let avail = 0;
      for (const po of poRefs) avail += running.get(po) ?? 0;

      const baseBalance = p.document.balanceDue ?? p.document.totalAmount ?? 0;
      const settlement = Math.max(row.controls.amountToPay.value ?? 0, 0);
      const cap = Math.round(Math.min(baseBalance, settlement, avail) * 100) / 100;
      row.controls.advanceAvailable.setValue(cap, { emitEvent: false });

      let use = isReopen ? (row.controls.advanceToUse.value ?? 0) : cap;
      use = Math.round(Math.max(0, Math.min(use, cap)) * 100) / 100;
      row.controls.advanceToUse.setValue(use, { emitEvent: false });

      let need = use;
      for (const po of poRefs) {
        if (need <= 0) break;
        const a = running.get(po) ?? 0;
        const take = Math.min(a, need);
        running.set(po, a - take);
        need -= take;
      }
    }
    // Re-add any previously-selected document the live payable search dropped (this is the terminal step of
    // a reopen), then total everything together.
    this.appendMissingPersistedRows();
    this.recomputeTotals();
  }

  /** Re-add read-only rows for a reopened request's persisted lines that the live payable search no longer
   * returns — e.g. an invoice fully paid by the request's own spawned payment, so it dropped out of the
   * "payable" set. Without this the selected document silently disappears on reopen (#4457). */
  private appendMissingPersistedRows(): void {
    const lines = this.reopenPersistedLines;
    this.reopenPersistedLines = [];
    if (!lines.length) return;

    // Every document reference the search-driven rows already cover (base docs + their grouped notes).
    const covered = new Set<string>();
    for (const p of this.payableByDoc.values()) for (const ref of payableGroupRefs(p)) covered.add(ref);

    for (const line of lines) {
      if (covered.has(line.apDocumentReference)) continue;
      const group = this.formBuilder.group<ApPaymentRequestLineFormGroup>({
        apDocumentReference: new FormControl<string>(line.apDocumentReference, { nonNullable: true }),
        invoiceNumber: new FormControl<string>(line.invoiceNumber ?? '', { nonNullable: true }),
        // From the line's own snapshot (#4788) — the document is gone from the payable set, so this is the
        // only place the provider's number still exists. Blank on lines persisted before the snapshot existed.
        fiscalNumber: new FormControl<string>(line.fiscalNumber ?? '', { nonNullable: true }),
        dueDate: new FormControl<string | null>(null),
        netPayable: new FormControl<number>(line.amountToPay, { nonNullable: true }),
        selected: new FormControl<boolean>(true, { nonNullable: true }),
        // Never gated: this row exists because the document already left the payable set (settled), so it is
        // part of the request's history rather than a new selection. The row is made read-only just below.
        withholdingBlocked: new FormControl<boolean>(false, { nonNullable: true }),
        amountToPay: new FormControl<number>(line.amountToPay, { nonNullable: true }),
        isCredit: new FormControl<boolean>(line.amountToPay < 0, { nonNullable: true }),
        // The document is gone from the payable set, so the line's own snapshot is the only rate left (#4797).
        exchangeRate: new FormControl<number>(line.exchangeRate || 1, { nonNullable: true }),
        currency: new FormControl<number>(line.currency ?? 0, { nonNullable: true }),
        docWithholding: new FormControl<number>(line.withholdingAmount ?? 0, { nonNullable: true }),
        advanceToUse: new FormControl<number>(line.advanceAppliedAmount ?? 0, { nonNullable: true }),
        advanceAvailable: new FormControl<number>(line.advanceAppliedAmount ?? 0, { nonNullable: true }),
        note: new FormControl<string | null>(line.note ?? null),
      });
      // Settled document: read-only. buildLines() still reads the raw values, so the line is preserved on save.
      group.disable({ emitEvent: false });
      this.lines.push(group);
    }
  }

  /** Cash requested for a row = amount to pay − advance to use (#4349). */
  cashToPay(line: FormGroup<ApPaymentRequestLineFormGroup>): number {
    const amount = line.controls.amountToPay.value ?? 0;
    const advance = Math.min(line.controls.advanceToUse.value ?? 0, Math.max(amount, 0));
    return Math.round((amount - advance) * 100) / 100;
  }

  /**
   * The line's total in functional currency: what it is being paid, converted at the rate its own document
   * was booked with. NOT the document's functional total — that covers the whole invoice, so a partial
   * payment would read as the full amount.
   */
  functionalFor(line: FormGroup<ApPaymentRequestLineFormGroup>): number {
    const amount = line.controls.amountToPay.value ?? 0;
    return Math.round(amount * (line.controls.exchangeRate.value || 1) * 100) / 100;
  }

  /** Withholding already applied to this document by a posted Constancia de Retención — informational: it
   * already reduced the balance due, so the payment settles the net directly and it is not re-deducted (#4515). */
  lineWithholding(line: FormGroup<ApPaymentRequestLineFormGroup>): number {
    return line.controls.docWithholding.value ?? 0;
  }

  /** The payable aggregate for a base-document row (undefined for a nested note row). */
  payableFor(docId: string | null | undefined): PayableDocument | undefined {
    return docId ? this.payableByDoc.get(docId) : undefined;
  }

  toggleExpand(docId: string | null | undefined): void {
    if (docId) this.expandedRows[docId] = !this.expandedRows[docId];
  }

  /** Read-only quick view of the underlying AP document (#4789) — works for the "Settled" rows too, since the
   * modal fetches by id rather than off the payable set. Informational, so the result is ignored. */
  openDocumentQuickView(docId: string | null | undefined): void {
    if (!docId) return;
    // Wide: the amounts block and the line table both carry a functional column, which is cramped at the
    // default width.
    this.modalService.open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId: docId }).subscribe();
  }

  /** Every row that would pay something, at the amount it would pay now — credits and blocked rows cannot.
   * Selected rows are checked together; an unselected one as a candidate, so its warning shows before it is
   * picked without counting against the rows that are. */
  private coverageRequests(): PoReceiptCoverageRequest[] {
    return this.lines.controls
      .filter((row) => !row.controls.isCredit.value && !row.controls.withholdingBlocked.value)
      .map((row) => ({
        documentReference: row.controls.apDocumentReference.value,
        amount: row.controls.amountToPay.value ?? 0,
        candidate: !row.controls.selected.value,
      }));
  }

  adjustmentTooltip(docId: string | null | undefined): string {
    const p = this.payableFor(docId);
    if (!p) return '';
    const parts: string[] = [];
    for (const dn of p.debitNotes) parts.push(`+ ${this.t('ApDocument_DebitNote', 'Debit note')} ${dn.number ?? ''}`);
    for (const cn of p.creditNotes) parts.push(`− ${this.t('ApDocument_CreditNote', 'Credit note')} ${cn.number ?? ''}`);
    if (p.withheldAmount > 0) parts.push(`− ${this.t('ApDocument_Withholding', 'Withholding')} ${p.withheldAmount.toFixed(2)}`);
    if ((p.document.paidAmount ?? 0) > 0) parts.push(this.t('ApPaymentRequest_PartialPaymentExists', 'A partial payment already exists'));
    return parts.join('\n');
  }

  private recomputeTotals(): void {
    let total = 0;
    let cash = 0;
    let advance = 0;
    let functional = 0;
    let advanceFunctional = 0;
    let cashFunctional = 0;
    let creditFunctional = 0;
    let chargeFunctional = 0;
    let selected = 0;
    const currencies = new Set<number>();
    for (const row of this.lines.controls) {
      if (row.controls.selected.value) {
        const amount = row.controls.amountToPay.value ?? 0;
        const adv = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(amount, 0));
        const rate = row.controls.exchangeRate.value || 1;
        currencies.add(row.controls.currency.value);
        // Raw sums stay for the single-currency case; the template hides them when the selection is mixed,
        // where they would be adding Lempiras to dollars.
        total += amount;
        advance += adv;
        cash += amount - adv;
        // Summed per line at each document's own rate — invoices booked on different days do not share one.
        functional += this.functionalFor(row);
        if (row.controls.isCredit.value) creditFunctional -= this.functionalFor(row);
        else chargeFunctional += this.functionalFor(row);
        advanceFunctional += Math.round(adv * rate * 100) / 100;
        cashFunctional += Math.round((amount - adv) * rate * 100) / 100;
        selected++;
      }
    }
    this.total.set(Math.round(total * 100) / 100);
    this.totalCash.set(Math.round(cash * 100) / 100);
    this.totalAdvance.set(Math.round(advance * 100) / 100);
    this.totalFunctional.set(Math.round(functional * 100) / 100);
    this.totalAdvanceFunctional.set(Math.round(advanceFunctional * 100) / 100);
    this.totalCashFunctional.set(Math.round(cashFunctional * 100) / 100);
    this.creditAppliedFunctional.set(Math.round(creditFunctional * 100) / 100);
    this.chargesFunctional.set(Math.round(chargeFunctional * 100) / 100);
    this.selectedCurrencies.set([...currencies].sort((a, b) => a - b));
    this.selectedCount.set(selected);
  }

  /**
   * True when the money cannot legally leave through the chosen account (#5144/#5142).
   *
   * <p>A payment settles out of an account in its own currency, and may only settle a document booked in a
   * different currency when the payment itself is in the FUNCTIONAL currency — that is the only rate a
   * document carries. So a selection that spans currencies, or whose currency differs from the destination,
   * has to leave through a functional-currency account. Flagged here rather than at post, where the API
   * refuses it after the request has already been approved.</p>
   */
  readonly destinationMismatch = computed(() => {
    const destination = this.destinationCurrency();
    const currencies = this.selectedCurrencies();
    if (destination === null || currencies.length === 0) return false;
    const allMatchDestination = currencies.every((c) => c === destination);
    return !allMatchDestination && destination !== this.functionalCurrency();
  });

  // ---- load existing ----
  private loadRequest(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.patchFromRequest(response.data);
        },
        error: (error) => console.error('Error loading AP payment request:', error),
      });
  }

  private patchFromRequest(data: ApPaymentRequest): void {
    this.status.set(data.status);
    // A mixed request has no header currency (#5135). The page already labels every row and total from the
    // LINES, so this only drives the places that need one figure's currency — the functional one, which is
    // what those figures are in.
    this.currency.set(data.currency ?? data.functionalCurrency);
    // From the persisted lines, so a request whose documents have all been settled (and therefore return no
    // payables) still labels its functional column correctly.
    if (data.lines?.length) this.functionalCurrency.set(data.lines[0].functionalCurrency ?? 0);
    this.headerTitle.set(`${this.t('ApPaymentRequest', 'Request')} ${data.number ?? ''}`.trim());
    this.breadcrumbService.setCurrent(data.number);
    this.spawnedPaymentId.set(data.spawnedApPaymentReference ?? null);
    this.cashRequestReferences.set(data.cashRequestReferences ?? []);

    const provider: Business = {
      id: data.providerReference,
      name: data.providerLegalName ?? '',
      code: data.providerCode,
      businessTypes: [BusinessEntityType.Provider],
    };
    this.selectedProvider.set(provider);
    this.form.patchValue(
      { provider, selectedProviderBankId: data.selectedProviderBankId ?? null, notes: data.notes ?? null },
      { emitEvent: false },
    );
    this.form.controls.provider.disable({ emitEvent: false }); // provider is locked after creation

    this.loadProviderBanks(provider);

    // Rebuild grouped lines from the live payable set, folding the request's saved per-document amounts (and the
    // per-base advance earmark) back in.
    const savedByRef = new Map<string, number>();
    const savedAdvanceByRef = new Map<string, number>();
    const savedNoteByRef = new Map<string, string>();
    for (const line of data.lines ?? []) {
      savedByRef.set(line.apDocumentReference, line.amountToPay);
      if ((line.advanceAppliedAmount ?? 0) > 0) savedAdvanceByRef.set(line.apDocumentReference, line.advanceAppliedAmount!);
      // The note was stamped onto every document of the group, so any member restores the group's note (#4787).
      if (line.note) savedNoteByRef.set(line.apDocumentReference, line.note);
    }
    // Keep the raw persisted lines so any document the live payable search no longer returns is re-added
    // read-only after the search rebuild, instead of silently vanishing from the request (#4457).
    this.reopenPersistedLines = data.lines ?? [];
    this.loadPayableInvoices(data.providerReference, savedByRef, savedAdvanceByRef, savedNoteByRef);
  }

  // ---- build payloads ----
  /** Expand each selected group's amount into per-document lines doc-first (#4516); the base line carries the
   * advance earmark (#4349). The payload stays one line per document — only the input was grouped. */
  private buildLines(): ApPaymentRequestLineInsert[] {
    const out: ApPaymentRequestLineInsert[] = [];
    for (const row of this.lines.controls) {
      if (!row.controls.selected.value) continue;
      const amount = row.controls.amountToPay.value ?? 0;
      if (amount === 0) continue;
      const payable = this.payableByDoc.get(row.controls.apDocumentReference.value);
      const advance = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(amount, 0));
      // The note belongs to the group the user typed it on, but the payload is per document — stamp it on
      // every member so the reason survives the doc-first split and the API sees it whichever line it lands on.
      const note = row.controls.note.value?.trim() || null;
      if (payable && isPayableBaseGroup(payable)) {
        for (const entry of distributePayable(payable, amount)) {
          const isBase = entry.apDocumentReference === (payable.document.id ?? '');
          out.push({
            apDocumentReference: entry.apDocumentReference,
            amountToPay: entry.amount,
            advanceAppliedAmount: isBase ? Math.min(advance, entry.amount) : 0,
            note,
          });
        }
      } else {
        // Standalone note, or a settled document re-added on reopen (#4457) — carry its own advance earmark
        // (notes always have 0; a re-added row keeps whatever was persisted).
        const advance = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(amount, 0));
        out.push({
          apDocumentReference: row.controls.apDocumentReference.value,
          amountToPay: amount,
          advanceAppliedAmount: advance,
          note,
        });
      }
    }
    return out;
  }

  /** A row pays something other than its net payable — the case that needs a reason (#4787). Compared on
   * magnitude so a credit row (negative on both sides) reads the same way. */
  hasVariance(line: FormGroup<ApPaymentRequestLineFormGroup>): boolean {
    if (!line.controls.selected.value) return false;
    const amount = line.controls.amountToPay.value ?? 0;
    if (amount === 0) return false;
    return Math.abs(Math.abs(amount) - Math.abs(line.controls.netPayable.value ?? 0)) > 0.01;
  }

  /** A row that owes an explanation and hasn't been given one. Drives both the inline error and the submit gate. */
  needsNote(line: FormGroup<ApPaymentRequestLineFormGroup>): boolean {
    return this.hasVariance(line) && !line.controls.note.value?.trim();
  }

  /**
   * @param requireNotes enforce the #4787 justification rule. Off while saving a draft — a draft is allowed to
   * be half-filled — and on at submit, which is the point the request goes to someone else to approve.
   */
  private validate(requireNotes = false): boolean {
    if (this.form.controls.provider.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.buildLines().length === 0) {
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    // A group's amount cannot exceed its net payable (credit rows are negative → compare on magnitude).
    const over = this.lines.controls.some(
      (r) => r.controls.selected.value && Math.abs(r.controls.amountToPay.value ?? 0) > Math.abs(r.controls.netPayable.value ?? 0) + 0.01,
    );
    if (over) return false;

    if (requireNotes) {
      const unexplained = this.lines.controls.filter((r) => this.needsNote(r));
      if (unexplained.length > 0) {
        for (const row of unexplained) row.controls.note.markAsTouched();
        GlobalMessageUtil.error(
          this.t('ApPaymentRequest_NoteRequired_Title', 'Justification required'),
          this.t('ApPaymentRequest_NoteRequired_Detail', 'Explain why these invoices pay an amount other than what is owed: ') +
            unexplained.map((r) => r.controls.invoiceNumber.value).join(', '),
        );
        return false;
      }
    }
    return true;
  }

  // ---- actions ----
  saveDraft(): void {
    if (!this.validate()) return;
    this.startLoading();

    const id = this.currentId();
    if (!this.isNew() && id) {
      const update: ApPaymentRequestUpdate = {
        selectedProviderBankId: this.form.controls.selectedProviderBankId.value,
        notes: this.form.controls.notes.value,
        lines: this.buildLines(),
      };
      this.service
        .update(id, update)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (response.data) this.patchFromRequest(response.data);
          },
          error: (error) => console.error('Error updating AP payment request:', error),
        });
      return;
    }

    const provider = this.form.controls.provider.value;
    const insert: ApPaymentRequestInsert = {
      financialCompanyReference: '', // API resolves from the current site
      providerReference: provider?.id ?? '',
      selectedProviderBankId: this.form.controls.selectedProviderBankId.value,
      notes: this.form.controls.notes.value,
      lines: this.buildLines(),
    };
    this.service
      .createDraft(insert)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const created = response.data;
          if (created?.id) {
            this.isNew.set(false);
            this.currentId.set(created.id);
            this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
            this.patchFromRequest(created);
          }
        },
        error: (error) => console.error('Error creating AP payment request:', error),
      });
  }

  submit(): void {
    const id = this.currentId();
    if (!id) return;
    // Catch a missing justification here rather than letting the API reject it (#4787) — the rule is about a
    // choice the user just made on screen, so the feedback belongs on the row. The API re-checks regardless.
    if (!this.validate(true)) return;
    this.confirmationService.confirm({
      header: this.t('ApPaymentRequest_Submit_Header', 'Submit for approval'),
      message: this.t('ApPaymentRequest_Submit_Message', 'Submit this request for approval?'),
      icon: 'pi pi-send',
      accept: () => this.runAction(this.service.submit(id)),
    });
  }

  approve(): void {
    const id = this.currentId();
    if (!id) return;
    // Mirrors the API, which re-checks on approve: an approver can edit the lines in PendingApproval, so the
    // rule has to hold here too and not only on the requester's submit (#4787).
    if (!this.validate(true)) return;
    this.confirmationService.confirm({
      header: this.t('ApPaymentRequest_Approve_Header', 'Approve request'),
      message: this.t('ApPaymentRequest_Approve_Message', 'Approving creates a draft AP payment for the executing department. Continue?'),
      icon: 'pi pi-check',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.runAction(this.service.approve(id)),
    });
  }

  reject(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApPaymentRequest_Reject_Header', 'Reject request'),
      message: this.t('ApPaymentRequest_Reject_Message', 'Reject this payment request?'),
      icon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.runAction(this.service.reject(id)),
    });
  }

  cancel(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApPaymentRequest_Cancel_Header', 'Cancel request'),
      message: this.t('ApPaymentRequest_Cancel_Message', 'Cancel this payment request?'),
      icon: 'pi pi-ban',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.runAction(this.service.cancel(id)),
    });
  }

  private runAction(action: ReturnType<ApPaymentRequestProxyService['submit']>): void {
    this.startLoading();
    action.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        if (response.data) this.patchFromRequest(response.data);
      },
      error: (error) => console.error('Error on AP payment request action:', error),
    });
  }

  openSpawnedPayment(): void {
    const paymentId = this.spawnedPaymentId();
    if (paymentId) this.router.navigate(['/ap-payment-details'], { queryParams: { id: paymentId } });
  }

  back(): void {
    this.router.navigate(['/ap-payment-requests']);
  }

  getRowGroup(row: unknown): FormGroup<ApPaymentRequestLineFormGroup> {
    return row as FormGroup<ApPaymentRequestLineFormGroup>;
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted — the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApPaymentRequest_Delete_Title', 'Delete draft'),
      message: this.t('ApPaymentRequest_Delete_Confirm', 'Delete this draft payment request? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('ApPaymentRequest_Delete_Done_Title', 'Draft deleted'),
          this.t('ApPaymentRequest_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/ap-payment-requests']);
      },
    });
  }
}
